import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { X, Landmark, Sparkles, CircleDollarSign, ShieldCheck } from 'lucide-react';

interface ProgrammeEssorModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type EssorTab = 'vue' | 'volets' | 'diagnostic' | 'admissibilite' | 'aide' | 'processus' | 'accompagnement' | 'demarrer';

export default function ProgrammeEssorModal({ isOpen, onClose }: ProgrammeEssorModalProps) {
  const [activeTab, setActiveTab] = useState<EssorTab>('vue');

  if (!isOpen) return null;

  const tabs: { id: EssorTab; label: string; highlight?: boolean }[] = [
    { id: 'vue', label: 'Vue d’ensemble' },
    { id: 'volets', label: 'Volets' },
    { id: 'diagnostic', label: 'Diagnostic numérique', highlight: true },
    { id: 'admissibilite', label: 'Admissibilité' },
    { id: 'aide', label: 'Aide financière' },
    { id: 'processus', label: 'Processus' },
    { id: 'accompagnement', label: 'Accompagnement BZ' },
    { id: 'demarrer', label: 'Démarrer' },
  ];

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50"
        onClick={onClose}
      />

      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-linear-to-br from-slate-900 via-emerald-950 to-slate-900 rounded-2xl w-[90vw] h-[90vh] border border-emerald-500/40 shadow-2xl flex flex-col overflow-hidden">
          <div className="flex items-start justify-between px-6 py-4 border-b border-emerald-500/30 bg-slate-950/70">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-emerald-500/20 border border-emerald-400/70 flex items-center justify-center shadow-lg">
                <Landmark className="w-6 h-6 text-emerald-200" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-emerald-300 font-semibold">
                  💰 Subventions & Financement
                </div>
                <h2 className="text-xl md:text-2xl font-bold text-white">Programme ESSOR</h2>
                <p className="text-xs md:text-sm text-emerald-100/80 mt-1">
                  Financer vos projets de transformation, productivité et croissance
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="ml-4 inline-flex items-center justify-center rounded-full bg-white/5 hover:bg-white/10 border border-white/20 p-2 text-white transition"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="px-6 pt-4 border-b border-emerald-500/20 bg-slate-950/60">
            <div className="flex flex-wrap gap-2 pb-4">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-3 py-2 rounded-lg text-sm font-semibold transition ${
                    activeTab === tab.id
                      ? 'bg-emerald-500/30 text-white border border-emerald-300/70'
                      : tab.highlight
                        ? 'bg-amber-500/10 text-amber-200 border border-amber-400/30 hover:bg-amber-500/20'
                        : 'bg-white/5 text-emerald-100/80 border border-white/10 hover:bg-white/10'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6 text-sm text-slate-100">
            <div className="grid md:grid-cols-2 gap-4">
              <button className="rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold px-4 py-3 transition text-left">
                Évaluer mon admissibilité
              </button>
              <button className="rounded-xl bg-white/5 hover:bg-white/10 border border-white/15 text-white font-semibold px-4 py-3 transition text-left">
                Démarrer un diagnostic PRISME
              </button>
            </div>

            {activeTab === 'vue' && (
              <div className="space-y-6">
                <Card title="Vue d’ensemble" icon={<Sparkles className="w-5 h-5 text-emerald-300" />}>
                  <p>
                    Le Programme ESSOR est un programme du gouvernement du Québec visant à soutenir les investissements des entreprises pour améliorer leur productivité, accélérer leur transformation numérique et soutenir leur croissance.
                  </p>
                </Card>

                <Card title="Objectifs du programme">
                  <BulletList items={[
                    'Accroître la productivité des entreprises',
                    'Favoriser la transformation numérique',
                    'Soutenir l’innovation et les investissements',
                    'Appuyer l’expansion et l’internationalisation',
                  ]} />
                </Card>

                <HighlightBox title="Positionnement BZ">
                  Chez BZ, nous utilisons le Programme ESSOR comme levier stratégique pour accélérer vos projets PRISME et réduire vos investissements initiaux.
                </HighlightBox>
              </div>
            )}

            {activeTab === 'volets' && (
              <div className="grid lg:grid-cols-2 gap-4">
                <VoletCard
                  title="Volet 1 - Diagnostic & faisabilité"
                  description="Études de faisabilité, diagnostic numérique, plan numérique"
                  useCase="Point d’entrée idéal pour PRISME et transformation numérique"
                />
                <VoletCard
                  title="Volet 2 - Investissement & productivité"
                  description="Projets d’investissement, modernisation, transformation technologique"
                  useCase="Implantation de solutions TI, ERP, automatisation"
                />
                <VoletCard
                  title="Volet 3 - Environnement"
                  description="Réduction de l’empreinte environnementale"
                  useCase="Optimisation énergétique, technologies propres"
                />
                <VoletCard
                  title="Volet 4 - Internationalisation"
                  description="Expansion vers les marchés internationaux"
                  useCase="Croissance et exportation"
                />
              </div>
            )}

            {activeTab === 'diagnostic' && (
              <div className="space-y-6">
                <HighlightBox title="Diagnostic numérique" accent="amber">
                  Le volet 1 permet de financer un diagnostic numérique complet et un plan de transformation.
                </HighlightBox>

                <Card title="Ce qui est couvert">
                  <BulletList items={[
                    'Diagnostic de maturité numérique',
                    'Analyse des processus d’affaires',
                    'Plan numérique stratégique',
                    'Priorisation des projets',
                    'Sélection de solutions technologiques',
                    'Plan de gestion du changement',
                  ]} />
                </Card>

                <div className="grid md:grid-cols-2 gap-4">
                  <Card title="Conditions">
                    <BulletList items={[
                      'Entreprise ≤ 250 employés',
                      'Chiffre d’affaires ≥ 2.5M$',
                      'Projet réalisé en ≤ 12 mois',
                    ]} />
                  </Card>

                  <Card title="Financement" icon={<CircleDollarSign className="w-5 h-5 text-emerald-300" />}>
                    <div className="space-y-2">
                      <p><strong className="text-white">Taux :</strong> Jusqu’à 50%</p>
                      <p><strong className="text-white">Maximum :</strong> 20 000$ à 50 000$</p>
                    </div>
                  </Card>
                </div>

                <HighlightBox title="Positionnement BZ">
                  Ce volet est parfaitement aligné avec notre démarche PRISME (bilan de santé TI + plan directeur).
                </HighlightBox>
              </div>
            )}

            {activeTab === 'admissibilite' && (
              <div className="grid lg:grid-cols-3 gap-4">
                <Card title="Entreprises admissibles" icon={<ShieldCheck className="w-5 h-5 text-emerald-300" />}>
                  <BulletList items={[
                    'Entreprises à but lucratif',
                    'Entreprises d’économie sociale',
                    'Immatriculées au Québec',
                  ]} />
                </Card>
                <Card title="Conditions clés">
                  <BulletList items={[
                    'Activités au Québec',
                    'Conformité (RENA, francisation)',
                    'Capacité financière démontrée',
                  ]} />
                </Card>
                <Card title="Non admissible">
                  <BulletList items={[
                    'Secteurs financiers, immobilier, télécom, etc.',
                    'Entreprises en défaut avec l’État',
                    'Secteurs sensibles (armes, jeux, etc.)',
                  ]} />
                </Card>
              </div>
            )}

            {activeTab === 'aide' && (
              <div className="space-y-6">
                <div className="grid md:grid-cols-3 gap-4">
                  <Card title="Types d’aide">
                    <BulletList items={[
                      'Subvention (non remboursable)',
                      'Prêt ou contribution remboursable',
                      'Garantie de prêt',
                    ]} />
                  </Card>
                  <Card title="Règles">
                    <BulletList items={[
                      'Apport privé minimum de 20%',
                      'Cumul maximal de 50% à 80%',
                      'Effet levier requis',
                    ]} />
                  </Card>
                  <Card title="Montants">
                    <BulletList items={[
                      'Volet 1 : jusqu’à 50 000$',
                      'Volet 2 : jusqu’à 10M$',
                      'Volet 3 : variable',
                      'Volet 4 : variable',
                    ]} />
                  </Card>
                </div>
              </div>
            )}

            {activeTab === 'processus' && (
              <div className="space-y-6">
                <Card title="Étapes du processus">
                  <BulletList items={[
                    'Analyse d’admissibilité',
                    'Définition du projet',
                    'Montage financier',
                    'Dépôt de la demande',
                    'Analyse par Investissement Québec',
                    'Acceptation et convention',
                    'Versements et suivi',
                  ]} />
                </Card>

                <HighlightBox title="Délais">
                  Analyse continue, durée variable selon projet.
                </HighlightBox>
              </div>
            )}

            {activeTab === 'accompagnement' && (
              <div className="space-y-6">
                <HighlightBox title="Approche BZ">
                  BZ agit comme partenaire stratégique pour maximiser vos chances d’obtenir le financement.
                </HighlightBox>

                <div className="grid md:grid-cols-2 gap-4">
                  <Card title="Services">
                    <BulletList items={[
                      'Diagnostic PRISME admissible ESSOR',
                      'Montage de dossier',
                      'Alignement stratégique du projet',
                      'Rédaction et structuration',
                      'Accompagnement complet jusqu’à l’acceptation',
                    ]} />
                  </Card>

                  <Card title="Valeur apportée">
                    <BulletList items={[
                      'Augmentation des chances d’acceptation',
                      'Optimisation du financement',
                      'Accélération des projets',
                    ]} />
                  </Card>
                </div>
              </div>
            )}

            {activeTab === 'demarrer' && (
              <div className="space-y-6">
                <HighlightBox title="Démarrer" accent="amber">
                  Vous pourriez être admissible à un financement important pour votre transformation numérique.
                </HighlightBox>

                <div className="grid md:grid-cols-3 gap-4">
                  <button className="rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold px-4 py-4 transition text-left">
                    Évaluer mon admissibilité
                  </button>
                  <button className="rounded-xl bg-white/5 hover:bg-white/10 border border-white/15 text-white font-semibold px-4 py-4 transition text-left">
                    Planifier un diagnostic PRISME
                  </button>
                  <button className="rounded-xl bg-white/5 hover:bg-white/10 border border-white/15 text-white font-semibold px-4 py-4 transition text-left">
                    Parler à un expert
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

function Card({ title, children, icon }: { title: string; children: React.ReactNode; icon?: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-5 space-y-3">
      <div className="flex items-center gap-2">
        {icon}
        <h3 className="text-lg font-bold text-white">{title}</h3>
      </div>
      <div className="text-slate-200/90 leading-relaxed">{children}</div>
    </div>
  );
}

function HighlightBox({ title, children, accent = 'emerald' }: { title: string; children: React.ReactNode; accent?: 'emerald' | 'amber' }) {
  const styles = accent === 'amber'
    ? 'border-amber-400/30 bg-amber-500/10 text-amber-50'
    : 'border-emerald-400/30 bg-emerald-500/10 text-emerald-50';

  return (
    <div className={`rounded-2xl border p-5 space-y-2 ${styles}`}>
      <h3 className="text-lg font-bold">{title}</h3>
      <div className="leading-relaxed">{children}</div>
    </div>
  );
}

function BulletList({ items }: { items: string[] }) {
  return (
    <ul className="space-y-2">
      {items.map((item) => (
        <li key={item} className="flex gap-2">
          <span className="text-emerald-300 mt-0.5">•</span>
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

function VoletCard({ title, description, useCase }: { title: string; description: string; useCase: string }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-5 space-y-3">
      <h3 className="text-lg font-bold text-white">{title}</h3>
      <p className="text-slate-200/90">{description}</p>
      <div className="rounded-xl bg-emerald-500/10 border border-emerald-400/20 p-3">
        <p className="text-xs uppercase tracking-wide text-emerald-300 font-semibold mb-1">Use case BZ</p>
        <p className="text-sm text-emerald-50">{useCase}</p>
      </div>
    </div>
  );
}
